import { pipe } from 'fp-ts/function';
import { array, ioOption } from 'fp-ts';
import { useQueryClient } from 'react-query';

import { useRestMutation } from 'utils/hooks/api/useRestMutation';

import { MessageType } from '../../domain';
import { UserMetadataQueryResponse } from '../../hooks/useUserMetadataQuery';
import { getMetadata, mergeMetadata, updateMetadata } from './utils';

const MODIFY_MESSAGE_TYPE_SUBSCRIPTIONS = '/users/profiles/modifyMessageTypeSubscriptions';

type ModifyMessageTypeSubscriptionsMutationRequest = {
  messageTypeIds: MessageType['id'][];
  userKey: string;
  subscribe: boolean;
};

const updateSubscribedMessageTypes =
  (
    isDoubleOptIn: boolean,
    rawSubscribes: MessageType['id'][] = [],
    unsubscribes: MessageType['id'][] = [],
  ) =>
  (value: UserMetadataQueryResponse): Partial<UserMetadataQueryResponse> => {
    const existingPending: number[] = value.pendingSubscriptionsMessageTypeIds ?? [];
    // if we're doing DOI, move added subscribes to pending
    const newSubscribes = isDoubleOptIn ? [] : rawSubscribes;
    const newPending = isDoubleOptIn ? rawSubscribes : [];

    return pipe(
      value.subscribedMessageTypeIds,
      // remove newly unsubscribed types from previously subscribed (if any)
      array.filter((messageTypeId) => !unsubscribes.includes(messageTypeId)),
      // merge newly subscribed with remaining subscribed
      (existing) => [...existing, ...newSubscribes],
      // merge values into partial update
      (subscribedMessageTypeIds) => ({
        subscribedMessageTypeIds,
        pendingSubscriptionsMessageTypeIds: [...existingPending, ...newPending],
      }),
    );
  };

const updateUnsubscribedMessageTypes =
  (unsubscribes: MessageType['id'][] = [], subscribes: MessageType['id'][] = []) =>
  (value: UserMetadataQueryResponse): Partial<UserMetadataQueryResponse> =>
    pipe(
      value.unsubscribedMessageTypeIds,
      // remove newly subscribed from previously unsubscribed
      array.filter((messageTypeId) => !subscribes.includes(messageTypeId)),
      // merge newly unsubscribed with remaining unsubscribed
      (types) => [...types, ...unsubscribes],
      // make a partial update
      (unsubscribedMessageTypeIds) => ({ unsubscribedMessageTypeIds }),
    );

export const useModifyMessageTypeSubscriptionsMutation = (subscribingForDoubleOptIn = false) => {
  const queryClient = useQueryClient();
  return useRestMutation<unknown, ModifyMessageTypeSubscriptionsMutationRequest>(
    MODIFY_MESSAGE_TYPE_SUBSCRIPTIONS,
    undefined,
    {
      onSuccess: (_data, { data }) => {
        const subscribedTypes = data.subscribe ? data.messageTypeIds : [];
        const unsubscribedTypes = data.subscribe ? [] : data.messageTypeIds;

        const program = pipe(
          data.userKey,
          getMetadata(queryClient), // get Metadata saved in query cache
          ioOption.fromNullable,
          ioOption.map(
            // if we have data, mutate it by merging partial updates
            mergeMetadata(
              updateSubscribedMessageTypes(
                subscribingForDoubleOptIn,
                subscribedTypes,
                unsubscribedTypes,
              ),
            ),
          ),
          ioOption.map(
            // if we have data, mutate it by merging partial updates
            mergeMetadata(updateUnsubscribedMessageTypes(unsubscribedTypes, subscribedTypes)),
          ),
          ioOption.tapIO(updateMetadata(data.userKey, queryClient)), // if we have data, mutate what is saved in query cache
        );

        program(); // Because we're using the IO type, we have to run the pipeline
      },
    },
  );
};
